import React, { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { Row, Col, Form, Spinner, Badge, Table } from 'react-bootstrap'
import Message from '../components/Message'
import axios from '../axios'
import {
    listLeagues, getLeagueDetails, createLeague, updateLeague, deleteLeague,
    registerPlayer, removePlayer, generateSchedule, updateMatchup, toggleMatchupLock, approvePlayer,
} from '../actions/leagueActions'
import { listUsers } from '../actions/userActions'
import { LEAGUE_CREATE_RESET, LEAGUE_UPDATE_RESET } from '../constants/leagueConstants'

const inputStyle = {
    fontSize: '0.875rem', border: '1.5px solid #e5e7eb',
    borderRadius: 8, padding: '0.5rem 0.75rem',
}

const Btn = ({ onClick, disabled, color = 'var(--bnb-navy)', children, small }) => (
    <button onClick={onClick} disabled={disabled} style={{
        background: color, color: '#fff', border: 'none',
        borderRadius: 8, padding: small ? '0.3rem 0.8rem' : '0.55rem 1.25rem',
        fontSize: small ? '0.75rem' : '0.875rem', fontWeight: 700, cursor: 'pointer',
        opacity: disabled ? 0.6 : 1,
    }}>{children}</button>
)

function LeagueAdminScreen() {
    const { id } = useParams()
    const dispatch = useDispatch()
    const navigate = useNavigate()

    const [view, setView]               = useState(id ? 'detail' : 'list') // list | create | detail
    const [form, setForm]               = useState({ name: '', season: 'spring', year: new Date().getFullYear(), description: '', status: 'draft', start_date: '', end_date: '' })
    const [selectedLeagueId, setSelectedLeagueId] = useState(id ? Number(id) : null)
    const [addUserId, setAddUserId]     = useState('')
    const [questions, setQuestions]     = useState([])
    const [qFilter, setQFilter]         = useState('pending')
    const [qModal, setQModal]           = useState(null)  // question object being edited
    const [qEdit, setQEdit]             = useState({ faq_title: '', answer: '' })

    const { userLogin: { userInfo } }   = useSelector(s => s)
    const { leagues = [], loading: listLoading } = useSelector(s => s.leagueList)
    const { league, loading: detailLoading }     = useSelector(s => s.leagueDetails)
    const { success: createSuccess, league: created } = useSelector(s => s.leagueCreate)
    const { success: updateSuccess }    = useSelector(s => s.leagueUpdate)
    const { loading: scheduleLoading, success: scheduleSuccess } = useSelector(s => s.leagueGenerateSchedule)
    const { users = [] }                = useSelector(s => s.userList)

    useEffect(() => { dispatch(listLeagues()); dispatch(listUsers()) }, [dispatch])

    useEffect(() => {
        if (userInfo?.token) {
            axios.get('/api/league/questions/', { headers: { Authorization: `Bearer ${userInfo.token}` } })
                .then(res => setQuestions(res.data)).catch(() => {})
        }
    }, [userInfo])

    const handleResolve = async (qId, resolution) => {
        try {
            await axios.put(`/api/league/questions/${qId}/resolve/`, {
                resolution,
                faq_title: qEdit.faq_title,
                answer:    qEdit.answer,
            }, { headers: { Authorization: `Bearer ${userInfo.token}`, 'Content-Type': 'application/json' } })
            const { data } = await axios.get('/api/league/questions/', { headers: { Authorization: `Bearer ${userInfo.token}` } })
            setQuestions(data)
            setQModal(null)
        } catch { /* silent */ }
    }

    useEffect(() => {
        if (id) { setSelectedLeagueId(Number(id)); setView('detail'); dispatch(getLeagueDetails(id)) }
    }, [id, dispatch])

    useEffect(() => {
        if (createSuccess && created) {
            dispatch({ type: LEAGUE_CREATE_RESET })
            navigate(`/admin/leagues/${created.id}`)
        }
    }, [createSuccess, created, navigate, dispatch])

    useEffect(() => {
        if (updateSuccess) { dispatch({ type: LEAGUE_UPDATE_RESET }); dispatch(getLeagueDetails(selectedLeagueId)) }
    }, [updateSuccess, dispatch, selectedLeagueId])

    useEffect(() => {
        if (scheduleSuccess && selectedLeagueId) dispatch(getLeagueDetails(selectedLeagueId))
    }, [scheduleSuccess, dispatch, selectedLeagueId])

    useEffect(() => {
        if (league && view === 'detail') {
            setForm({
                name: league.name || '', season: league.season || 'spring',
                year: league.year || new Date().getFullYear(),
                description: league.description || '', status: league.status || 'draft',
                start_date: league.start_date || '', end_date: league.end_date || '',
            })
        }
    }, [league, view])

    const handleSelectLeague = (l) => {
        setSelectedLeagueId(l.id)
        setView('detail')
        navigate(`/admin/leagues/${l.id}`)
        dispatch(getLeagueDetails(l.id))
    }

    const handleSave = () => {
        if (view === 'create') dispatch(createLeague(form))
        else dispatch(updateLeague(selectedLeagueId, form))
    }

    const handleAddPlayer = () => {
        if (!addUserId) return
        dispatch(registerPlayer(selectedLeagueId, addUserId))
        setTimeout(() => { dispatch(getLeagueDetails(selectedLeagueId)); setAddUserId('') }, 500)
    }

    const handleRemovePlayer = (userId) => {
        dispatch(removePlayer(selectedLeagueId, userId))
        setTimeout(() => dispatch(getLeagueDetails(selectedLeagueId)), 500)
    }

    const handleGenerateSchedule = () => {
        if (window.confirm('This will regenerate the entire regular-season schedule. Continue?'))
            dispatch(generateSchedule(selectedLeagueId))
    }

    const handleToggleLock = (matchupId) => {
        dispatch(toggleMatchupLock(matchupId))
        setTimeout(() => dispatch(getLeagueDetails(selectedLeagueId)), 400)
    }

    const handleMatchupByeToggle = (matchup) => {
        dispatch(updateMatchup(matchup.id, { is_bye: !matchup.is_bye }))
        setTimeout(() => dispatch(getLeagueDetails(selectedLeagueId)), 400)
    }

    const registeredUserIds = league?.registrations?.map(r => r.user) || []
    const unregisteredUsers = users.filter(u => !registeredUserIds.includes(u.id))

    const rounds = league?.matchups
        ? [...new Set(league.matchups.map(m => m.round_number))].sort((a, b) => a - b)
        : []

    const containerStyle = { paddingTop: '0.5rem' }
    const cardStyle = { background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', padding: '1.5rem', marginBottom: '1.5rem' }
    const sectionHead = (icon, label) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', borderBottom: '2px solid var(--bnb-navy)', paddingBottom: '0.4rem', marginBottom: '1.25rem', marginTop: '1.5rem' }}>
            <i className={`fa-solid ${icon}`} style={{ color: 'var(--bnb-gold)', fontSize: '0.95rem' }} />
            <span style={{ fontSize: '0.7rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--bnb-navy)' }}>{label}</span>
        </div>
    )

    return (
        <>
        <div style={containerStyle}>

            {/* ── Header ── */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
                <div>
                    <div style={{ fontSize: '0.65rem', letterSpacing: '0.15em', textTransform: 'uppercase', color: '#6b7280' }}>Admin</div>
                    <h2 style={{ fontWeight: 900, color: 'var(--bnb-navy)', margin: 0 }}>
                        <i className="fa-solid fa-ranking-star me-2" style={{ color: 'var(--bnb-gold)' }} />
                        Leagues
                    </h2>
                </div>
                <Btn onClick={() => { setView('create'); setForm({ name: '', season: 'spring', year: new Date().getFullYear(), description: '', status: 'draft', start_date: '', end_date: '' }); navigate('/admin/leagues') }}>
                    <i className="fa-solid fa-plus me-2" />New League
                </Btn>
            </div>

            <Row>
                {/* ── Left: League list ── */}
                <Col md={3}>
                    <div style={cardStyle}>
                        <div style={{ fontWeight: 800, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--bnb-navy)', marginBottom: '1rem' }}>All Leagues</div>
                        {listLoading ? <Spinner animation="border" size="sm" /> : (
                            leagues.length === 0
                                ? <p style={{ fontSize: '0.85rem', color: '#6b7280' }}>No leagues yet.</p>
                                : leagues.map(l => (
                                    <div key={l.id} onClick={() => handleSelectLeague(l)}
                                        style={{ padding: '0.6rem 0.75rem', borderRadius: 8, cursor: 'pointer', marginBottom: '0.4rem', background: selectedLeagueId === l.id ? 'var(--bnb-navy)' : '#f9fafb', color: selectedLeagueId === l.id ? '#fff' : '#111' }}>
                                        <div style={{ fontWeight: 700, fontSize: '0.85rem' }}>{l.name}</div>
                                        <div style={{ fontSize: '0.72rem', opacity: 0.75 }}>{l.season} {l.year} · {l.status}</div>
                                    </div>
                                ))
                        )}
                    </div>

                    {/* ── Submitted Questions ── */}
                    <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', padding: '1.25rem', marginTop: '1rem' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                            <div style={{ fontWeight: 800, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--bnb-navy)' }}>
                                <i className="fa-solid fa-circle-question me-1" style={{ color: 'var(--bnb-gold)' }} />
                                FAQ Questions
                                {questions.filter(q => q.resolution === 'pending').length > 0 && (
                                    <span style={{ marginLeft: '0.4rem', background: '#dc2626', color: '#fff', borderRadius: 999, padding: '0.05rem 0.45rem', fontSize: '0.65rem', fontWeight: 800 }}>
                                        {questions.filter(q => q.resolution === 'pending').length}
                                    </span>
                                )}
                            </div>
                            <div style={{ display: 'flex', gap: '0.25rem' }}>
                                {['pending', 'all'].map(f => (
                                    <button key={f} onClick={() => setQFilter(f)}
                                        style={{ padding: '0.15rem 0.5rem', borderRadius: 6, fontSize: '0.68rem', fontWeight: 700, cursor: 'pointer', border: `1.5px solid ${qFilter === f ? 'var(--bnb-navy)' : '#e5e7eb'}`, background: qFilter === f ? 'var(--bnb-navy)' : '#f9fafb', color: qFilter === f ? '#fff' : '#374151' }}>
                                        {f === 'pending' ? 'Pending' : 'Added'}
                                    </button>
                                ))}
                            </div>
                        </div>
                        {(() => {
                            const filtered = qFilter === 'pending' ? questions.filter(q => q.resolution === 'pending') : questions.filter(q => q.resolution !== 'dismissed')
                            if (filtered.length === 0) return <p style={{ fontSize: '0.8rem', color: '#9ca3af', margin: 0 }}>No questions {qFilter === 'pending' ? 'pending' : 'yet'}.</p>
                            return filtered.map(q => (
                                <div key={q.id}
                                    onClick={() => { setQModal(q); setQEdit({ faq_title: q.faq_title || q.question, answer: q.answer || '' }) }}
                                    style={{ padding: '0.75rem', borderRadius: 8, border: '1px solid #e5e7eb', marginBottom: '0.5rem', background: '#fff', cursor: 'pointer' }}
                                    onMouseEnter={e => { e.currentTarget.style.background = '#f0f4ff' }}
                                    onMouseLeave={e => { e.currentTarget.style.background = '#fff' }}
                                >
                                    <div style={{ fontSize: '0.82rem', fontWeight: 600, color: '#111827', marginBottom: '0.25rem' }}>{q.question}</div>
                                    <div style={{ fontSize: '0.7rem', color: '#6b7280', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                        <span>{q.asked_by} · {q.created_at}</span>
                                        {q.resolution !== 'pending' ? (
                                            <span style={{ padding: '0.1rem 0.4rem', borderRadius: 999, fontSize: '0.65rem', fontWeight: 700, background: q.resolution === 'added' ? '#d1fae5' : '#fee2e2', color: q.resolution === 'added' ? '#065f46' : '#dc2626' }}>
                                                {q.resolution === 'added' ? '✓ FAQ' : '✗ Dismissed'}
                                            </span>
                                        ) : (
                                            <span style={{ fontSize: '0.65rem', color: 'var(--bnb-navy)', fontWeight: 700 }}>Click to review →</span>
                                        )}
                                    </div>
                                </div>
                            ))
                        })()}
                    </div>
                </Col>

                {/* ── Right: Detail / Create ── */}
                <Col md={9}>
                    {(view === 'create' || view === 'detail') && (
                        <div style={cardStyle}>
                            <div style={{ fontWeight: 900, fontSize: '1.1rem', color: 'var(--bnb-navy)', marginBottom: '1.25rem' }}>
                                {view === 'create' ? 'New League' : league?.name}
                            </div>

                            {/* ── League Form ── */}
                            {sectionHead('fa-pen', 'League Info')}
                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label style={{ fontSize: '0.8rem', fontWeight: 700 }}>Name</Form.Label>
                                        <Form.Control style={inputStyle} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label style={{ fontSize: '0.8rem', fontWeight: 700 }}>Season</Form.Label>
                                        <Form.Select style={inputStyle} value={form.season} onChange={e => setForm({ ...form, season: e.target.value })}>
                                            <option value="spring">Spring</option>
                                            <option value="fall">Fall</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label style={{ fontSize: '0.8rem', fontWeight: 700 }}>Year</Form.Label>
                                        <Form.Control style={inputStyle} type="number" value={form.year} onChange={e => setForm({ ...form, year: e.target.value })} />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label style={{ fontSize: '0.8rem', fontWeight: 700 }}>Status</Form.Label>
                                        <Form.Select style={inputStyle} value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
                                            <option value="draft">Draft</option>
                                            <option value="active">Active</option>
                                            <option value="completed">Completed</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label style={{ fontSize: '0.8rem', fontWeight: 700 }}>Start Date</Form.Label>
                                        <Form.Control style={inputStyle} type="date" value={form.start_date} onChange={e => setForm({ ...form, start_date: e.target.value })} />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label style={{ fontSize: '0.8rem', fontWeight: 700 }}>End Date</Form.Label>
                                        <Form.Control style={inputStyle} type="date" value={form.end_date} onChange={e => setForm({ ...form, end_date: e.target.value })} />
                                    </Form.Group>
                                </Col>
                                <Col md={12}>
                                    <Form.Group className="mb-3">
                                        <Form.Label style={{ fontSize: '0.8rem', fontWeight: 700 }}>Description</Form.Label>
                                        <Form.Control as="textarea" rows={3} style={inputStyle} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Btn onClick={handleSave}>
                                <i className="fa-solid fa-floppy-disk me-2" />
                                {view === 'create' ? 'Create League' : 'Save Changes'}
                            </Btn>

                            {view === 'detail' && league && (
                                <>
                                    {/* ── Players ── */}
                                    {sectionHead('fa-users', `Players (${league.registrations?.length || 0})`)}
                                    <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem', alignItems: 'center' }}>
                                        <Form.Select style={{ ...inputStyle, width: 'auto', minWidth: 200 }} value={addUserId} onChange={e => setAddUserId(e.target.value)}>
                                            <option value="">-- Add Player --</option>
                                            {unregisteredUsers.map(u => (
                                                <option key={u.id} value={u.id}>{u.name || u.email}</option>
                                            ))}
                                        </Form.Select>
                                        <Btn onClick={handleAddPlayer} small disabled={!addUserId}>
                                            <i className="fa-solid fa-plus me-1" />Add
                                        </Btn>
                                    </div>
                                    <Table size="sm" hover style={{ fontSize: '0.85rem' }}>
                                        <thead><tr><th>Player</th><th>Email</th><th>Handicap</th><th>Home Course</th><th>Status</th><th></th></tr></thead>
                                        <tbody>
                                            {league.registrations?.map(r => (
                                                <tr key={r.id}>
                                                    <td style={{ fontWeight: 600 }}>{r.player_name}</td>
                                                    <td>{r.player_email}</td>
                                                    <td>{r.player_handicap ?? '—'}</td>
                                                    <td>{r.home_course_name || '—'}</td>
                                                    <td>
                                                        <span style={{ padding: '0.15rem 0.5rem', borderRadius: 999, fontSize: '0.72rem', fontWeight: 700, background: r.status === 'approved' ? '#d1fae5' : '#fef3c7', color: r.status === 'approved' ? '#065f46' : '#92400e' }}>
                                                            {r.status}
                                                        </span>
                                                    </td>
                                                    <td style={{ display: 'flex', gap: '0.3rem' }}>
                                                        {r.status !== 'approved' && (
                                                            <button onClick={() => { dispatch(approvePlayer(selectedLeagueId, r.user, 'approved')); setTimeout(() => dispatch(getLeagueDetails(selectedLeagueId)), 400) }}
                                                                style={{ background: '#d1fae5', color: '#065f46', border: 'none', borderRadius: 6, padding: '0.2rem 0.5rem', fontSize: '0.72rem', fontWeight: 700, cursor: 'pointer' }}>
                                                                ✓ Approve
                                                            </button>
                                                        )}
                                                        <button onClick={() => handleRemovePlayer(r.user)}
                                                            style={{ background: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: 6, padding: '0.2rem 0.5rem', fontSize: '0.75rem', cursor: 'pointer' }}>
                                                            Remove
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>

                                    {/* ── Schedule ── */}
                                    {sectionHead('fa-calendar-days', 'Schedule')}
                                    {(() => {
                                        // Schedule can only be generated in the 30-day window between reg close and league start
                                        const startDate = league.start_date ? new Date(league.start_date + 'T00:00:00') : null
                                        const cutoff    = startDate ? new Date(startDate.getTime()) : null
                                        if (cutoff) cutoff.setDate(cutoff.getDate() - 30)
                                        const now = new Date()
                                        const inWindow = startDate && cutoff && now >= cutoff && now < startDate
                                        const tooEarly = startDate && cutoff && now < cutoff
                                        const tooLate  = startDate && now >= startDate

                                        const hint = tooEarly
                                            ? `Available from ${cutoff.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} (30 days before start)`
                                            : tooLate
                                            ? 'League has already started — schedule is locked'
                                            : 'Regenerating will replace all existing regular-season matchups.'

                                        return (
                                            <div style={{ marginBottom: '1rem' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                                                    <Btn onClick={handleGenerateSchedule} disabled={scheduleLoading || !inWindow} color={inWindow ? '#059669' : '#9ca3af'}>
                                                        {scheduleLoading ? <Spinner animation="border" size="sm" /> : <><i className="fa-solid fa-shuffle me-2" />Generate Schedule</>}
                                                    </Btn>
                                                    <span style={{ fontSize: '0.75rem', color: tooEarly || tooLate ? '#dc2626' : '#6b7280' }}>
                                                        {hint}
                                                    </span>
                                                </div>
                                            </div>
                                        )
                                    })()}

                                    {rounds.map(roundNum => {
                                        const roundMatchups = league.matchups.filter(m => m.round_number === roundNum)
                                        return (
                                            <div key={roundNum} style={{ marginBottom: '1.25rem' }}>
                                                <div style={{ fontWeight: 800, fontSize: '0.8rem', textTransform: 'uppercase', color: 'var(--bnb-navy)', marginBottom: '0.5rem', letterSpacing: '0.06em' }}>
                                                    Round {roundNum} {roundMatchups[0]?.round_type === 'playoff' && <Badge bg="warning" text="dark">Playoff</Badge>}
                                                </div>
                                                <Table size="sm" hover style={{ fontSize: '0.83rem' }}>
                                                    <thead><tr><th>Home</th><th>Away</th><th>Course</th><th>Date</th><th>Result</th><th>Locked</th><th>Bye</th></tr></thead>
                                                    <tbody>
                                                        {roundMatchups.map(m => (
                                                            <tr key={m.id}>
                                                                <td style={{ fontWeight: 600 }}>{m.home_player_name}</td>
                                                                <td>{m.is_bye ? <Badge bg="secondary">BYE</Badge> : m.away_player_name}</td>
                                                                <td style={{ fontSize: '0.75rem', color: '#6b7280' }}>{m.home_course_name || '—'}</td>
                                                                <td>
                                                                    <input type="date" defaultValue={m.round_date || ''} style={{ fontSize: '0.75rem', border: '1px solid #e5e7eb', borderRadius: 4, padding: '0.1rem 0.3rem' }}
                                                                        onBlur={e => dispatch(updateMatchup(m.id, { round_date: e.target.value }))} />
                                                                </td>
                                                                <td>
                                                                    <Badge bg={m.result === 'pending' ? 'secondary' : m.result === 'home_win' || m.result === 'away_win' ? 'success' : 'warning'} text={m.result === 'tie' ? 'dark' : undefined}>
                                                                        {m.result}
                                                                    </Badge>
                                                                </td>
                                                                <td>
                                                                    <button onClick={() => handleToggleLock(m.id)} style={{ background: m.is_locked ? '#fde68a' : '#d1fae5', color: m.is_locked ? '#92400e' : '#065f46', border: 'none', borderRadius: 6, padding: '0.2rem 0.6rem', fontSize: '0.72rem', cursor: 'pointer' }}>
                                                                        {m.is_locked ? '🔒 Locked' : '🔓 Open'}
                                                                    </button>
                                                                </td>
                                                                <td>
                                                                    <button onClick={() => handleMatchupByeToggle(m)} style={{ background: m.is_bye ? '#fee2e2' : '#f3f4f6', color: m.is_bye ? '#dc2626' : '#374151', border: 'none', borderRadius: 6, padding: '0.2rem 0.6rem', fontSize: '0.72rem', cursor: 'pointer' }}>
                                                                        {m.is_bye ? 'Yes' : 'No'}
                                                                    </button>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </Table>
                                            </div>
                                        )
                                    })}
                                </>
                            )}
                        </div>
                    )}
                </Col>
            </Row>
        </div>

            {/* ── FAQ Question Modal ── */}
            {qModal && (
                <div onClick={() => setQModal(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
                    <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 14, width: '100%', maxWidth: 520, overflow: 'hidden', boxShadow: '0 24px 60px rgba(0,0,0,0.3)' }}>
                        <div style={{ background: 'var(--bnb-navy)', padding: '1rem 1.25rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div style={{ color: '#fff', fontWeight: 900, fontSize: '1rem' }}>
                                <i className="fa-solid fa-circle-question me-2" style={{ color: 'var(--bnb-gold)' }} />Review FAQ Question
                            </div>
                            <button onClick={() => setQModal(null)} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', width: 30, height: 30, borderRadius: '50%', fontSize: '1rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
                        </div>
                        <div style={{ padding: '1.5rem' }}>
                            <div style={{ fontSize: '0.72rem', color: '#6b7280', marginBottom: '1.25rem' }}>
                                Asked by <strong>{qModal.asked_by}</strong> on {qModal.created_at}
                            </div>
                            <div style={{ marginBottom: '1rem' }}>
                                <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: '#374151', marginBottom: '0.3rem' }}>FAQ Question Title</label>
                                <input type="text" value={qEdit.faq_title}
                                    onChange={e => setQEdit(prev => ({ ...prev, faq_title: e.target.value }))}
                                    style={{ width: '100%', border: '1.5px solid #e5e7eb', borderRadius: 8, padding: '0.5rem 0.75rem', fontSize: '0.88rem', outline: 'none' }} />
                            </div>
                            <div style={{ marginBottom: '1.25rem' }}>
                                <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: '#374151', marginBottom: '0.3rem' }}>Answer</label>
                                <textarea rows={4} value={qEdit.answer}
                                    onChange={e => setQEdit(prev => ({ ...prev, answer: e.target.value }))}
                                    placeholder="Write the answer that will appear in the FAQ…"
                                    style={{ width: '100%', border: '1.5px solid #e5e7eb', borderRadius: 8, padding: '0.5rem 0.75rem', fontSize: '0.88rem', outline: 'none', resize: 'vertical', fontFamily: 'inherit' }} />
                            </div>
                            <div style={{ display: 'flex', gap: '0.75rem' }}>
                                <button onClick={() => handleResolve(qModal.id, 'added')} disabled={!qEdit.answer.trim()}
                                    style={{ flex: 1, padding: '0.6rem', background: qEdit.answer.trim() ? '#059669' : '#e5e7eb', color: qEdit.answer.trim() ? '#fff' : '#9ca3af', border: 'none', borderRadius: 8, fontWeight: 800, fontSize: '0.88rem', cursor: qEdit.answer.trim() ? 'pointer' : 'not-allowed' }}>
                                    {qModal?.resolution === 'added' ? '✓ Update FAQ' : '✓ Save & Add to FAQ'}
                                </button>
                                <button onClick={() => handleResolve(qModal.id, 'dismissed')}
                                    style={{ padding: '0.6rem 1.25rem', background: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: '0.88rem', cursor: 'pointer' }}>
                                    Dismiss
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    )
}

export default LeagueAdminScreen
